import { Component, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components';
import { usePrintPreview } from './usePrintPreview';

/**
 * Around the drawing viewer (#518 phase 3). The app has no error boundary, so
 * anything thrown while the viewer renders would unmount the whole page to a
 * blank screen — above all the viewer's own code failing to download, which is
 * what a deploy since the page was opened does to a lazily loaded file, and the
 * Punch tab is one people leave open all day. This catches it and says so, with
 * the two ways out: reload the page (which fetches the new files), or go back
 * to the list. Going back doesn't fix it: React.lazy remembers a failed import
 * until the page reloads, so opening the viewer again fails the same way — which
 * is why the message sends them to reload rather than to try again.
 */
export default class PunchDrawingBoundary extends Component<{ onClose: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? <ViewerFailed onClose={this.props.onClose} /> : this.props.children;
  }
}

function ViewerFailed({ onClose }: { onClose: () => void }) {
  const { closeRef } = usePrintPreview(onClose, false);
  return createPortal(
    <div className="pl-dw" role="dialog" aria-modal="true" aria-label="Drawings">
      <div className="pl-dw-bar">
        <button ref={closeRef} type="button" className="pl-link" onClick={onClose}>← Back to the list</button>
        <strong>Drawings</strong>
      </div>
      <div className="pl-dw-scroll">
        <div className="pl-error pl-row" role="alert">
          <span>The drawing viewer didn’t load. The Project Tracker may have been updated since this page was opened — reload the page to try again.</span>
          <Button variant="secondary" size="sm" onClick={() => window.location.reload()}>Reload the Page</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
